import { EXPRESSION_TYPES } from '@willow/types-iso';

import { functionFrom, FunctionLibrary, getFunctionSignature } from '../FunctionDefinition';

const equals = {
  [EXPRESSION_TYPES.duration.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: EXPRESSION_TYPES.duration },
      { name: 'right', type: EXPRESSION_TYPES.duration },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.total({ unit: 'seconds' }) === b.total({ unit: 'seconds' }),
    'Returns true if the two durations are equal (second-level precision).',
  ),
  [EXPRESSION_TYPES.zonedDateTime.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: EXPRESSION_TYPES.zonedDateTime },
      { name: 'right', type: EXPRESSION_TYPES.zonedDateTime },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.equals(b),
    'Returns true if the two ZonedDateTime values represent the same instant.',
  ),
  [EXPRESSION_TYPES.plainDate.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: EXPRESSION_TYPES.plainDate },
      { name: 'right', type: EXPRESSION_TYPES.plainDate },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.equals(b),
    'Returns true if the two dates are equal.',
  ),
  [EXPRESSION_TYPES.string.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: EXPRESSION_TYPES.string },
      { name: 'right', type: EXPRESSION_TYPES.string },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a === b,
    'Returns true if the two strings are equal.',
  ),
  [EXPRESSION_TYPES.boolean.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: EXPRESSION_TYPES.boolean },
      { name: 'right', type: EXPRESSION_TYPES.boolean },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a === b,
    'Returns true if the two boolean values are equal.',
  ),
  [EXPRESSION_TYPES.number.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: EXPRESSION_TYPES.number },
      { name: 'right', type: EXPRESSION_TYPES.number },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a === b,
    'Returns true if the two numbers are equal.',
  ),
  [EXPRESSION_TYPES.plainTime.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: EXPRESSION_TYPES.plainTime },
      { name: 'right', type: EXPRESSION_TYPES.plainTime },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.equals(b),
    'Returns true if the two times are equal.',
  ),
};

const gt = {
  [EXPRESSION_TYPES.duration.name]: functionFrom(
    'gt',
    [
      { name: 'left', type: EXPRESSION_TYPES.duration },
      { name: 'right', type: EXPRESSION_TYPES.duration },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.total({ unit: 'seconds' }) > b.total({ unit: 'seconds' }),
    'Returns true if the left duration is greater than the right duration.',
  ),
  [EXPRESSION_TYPES.zonedDateTime.name]: functionFrom(
    'gt',
    [
      { name: 'left', type: EXPRESSION_TYPES.zonedDateTime },
      { name: 'right', type: EXPRESSION_TYPES.zonedDateTime },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) > 0,
    'Returns true if the left ZonedDateTime is after the right ZonedDateTime.',
  ),
  [EXPRESSION_TYPES.plainDate.name]: functionFrom(
    'gt',
    [
      { name: 'left', type: EXPRESSION_TYPES.plainDate },
      { name: 'right', type: EXPRESSION_TYPES.plainDate },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'days' }) > 0,
    'Returns true if the left date is after the right date.',
  ),
  [EXPRESSION_TYPES.string.name]: functionFrom(
    'gt',
    [
      { name: 'left', type: EXPRESSION_TYPES.string },
      { name: 'right', type: EXPRESSION_TYPES.string },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.localeCompare(b) > 0,
    'Returns true if the left string is greater than the right string.',
  ),
  [EXPRESSION_TYPES.number.name]: functionFrom(
    'gt',
    [
      { name: 'left', type: EXPRESSION_TYPES.number },
      { name: 'right', type: EXPRESSION_TYPES.number },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a > b,
    'Returns true if the left number is greater than the right number.',
  ),
  [EXPRESSION_TYPES.plainTime.name]: functionFrom(
    'gt',
    [
      { name: 'left', type: EXPRESSION_TYPES.plainTime },
      { name: 'right', type: EXPRESSION_TYPES.plainTime },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) > 0,
    'Returns true if the left time is after the right time.',
  ),
};

const gte = {
  [EXPRESSION_TYPES.duration.name]: functionFrom(
    'gte',
    [
      { name: 'left', type: EXPRESSION_TYPES.duration },
      { name: 'right', type: EXPRESSION_TYPES.duration },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.total({ unit: 'seconds' }) >= b.total({ unit: 'seconds' }),
    'Returns true if the left duration is greater than or equal to the right duration.',
  ),
  [EXPRESSION_TYPES.zonedDateTime.name]: functionFrom(
    'gte',
    [
      { name: 'left', type: EXPRESSION_TYPES.zonedDateTime },
      { name: 'right', type: EXPRESSION_TYPES.zonedDateTime },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) >= 0,
    'Returns true if the left ZonedDateTime is after or equal to the right ZonedDateTime.',
  ),
  [EXPRESSION_TYPES.plainDate.name]: functionFrom(
    'gte',
    [
      { name: 'left', type: EXPRESSION_TYPES.plainDate },
      { name: 'right', type: EXPRESSION_TYPES.plainDate },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'days' }) >= 0,
    'Returns true if the left date is after or equal to the right date.',
  ),
  [EXPRESSION_TYPES.string.name]: functionFrom(
    'gte',
    [
      { name: 'left', type: EXPRESSION_TYPES.string },
      { name: 'right', type: EXPRESSION_TYPES.string },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.localeCompare(b) >= 0,
    'Returns true if the left string is greater than or equal to the right string.',
  ),
  [EXPRESSION_TYPES.number.name]: functionFrom(
    'gte',
    [
      { name: 'left', type: EXPRESSION_TYPES.number },
      { name: 'right', type: EXPRESSION_TYPES.number },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a >= b,
    'Returns true if the left number is greater than or equal to the right number.',
  ),
  [EXPRESSION_TYPES.plainTime.name]: functionFrom(
    'gte',
    [
      { name: 'left', type: EXPRESSION_TYPES.plainTime },
      { name: 'right', type: EXPRESSION_TYPES.plainTime },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) >= 0,
    'Returns true if the left time is after or equal to the right time.',
  ),
};

const lt = {
  [EXPRESSION_TYPES.duration.name]: functionFrom(
    'lt',
    [
      { name: 'left', type: EXPRESSION_TYPES.duration },
      { name: 'right', type: EXPRESSION_TYPES.duration },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.total({ unit: 'seconds' }) < b.total({ unit: 'seconds' }),
    'Returns true if the left duration is less than the right duration.',
  ),
  [EXPRESSION_TYPES.zonedDateTime.name]: functionFrom(
    'lt',
    [
      { name: 'left', type: EXPRESSION_TYPES.zonedDateTime },
      { name: 'right', type: EXPRESSION_TYPES.zonedDateTime },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) < 0,
    'Returns true if the left ZonedDateTime is before the right ZonedDateTime.',
  ),
  [EXPRESSION_TYPES.plainDate.name]: functionFrom(
    'lt',
    [
      { name: 'left', type: EXPRESSION_TYPES.plainDate },
      { name: 'right', type: EXPRESSION_TYPES.plainDate },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'days' }) < 0,
    'Returns true if the left date is before the right date.',
  ),
  [EXPRESSION_TYPES.string.name]: functionFrom(
    'lt',
    [
      { name: 'left', type: EXPRESSION_TYPES.string },
      { name: 'right', type: EXPRESSION_TYPES.string },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.localeCompare(b) < 0,
    'Returns true if the left string is less than the right string.',
  ),
  [EXPRESSION_TYPES.number.name]: functionFrom(
    'lt',
    [
      { name: 'left', type: EXPRESSION_TYPES.number },
      { name: 'right', type: EXPRESSION_TYPES.number },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a < b,
    'Returns true if the left number is less than the right number.',
  ),
  [EXPRESSION_TYPES.plainTime.name]: functionFrom(
    'lt',
    [
      { name: 'left', type: EXPRESSION_TYPES.plainTime },
      { name: 'right', type: EXPRESSION_TYPES.plainTime },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) < 0,
    'Returns true if the left time is before the right time.',
  ),
};

const lte = {
  [EXPRESSION_TYPES.duration.name]: functionFrom(
    'lte',
    [
      { name: 'left', type: EXPRESSION_TYPES.duration },
      { name: 'right', type: EXPRESSION_TYPES.duration },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.total({ unit: 'seconds' }) <= b.total({ unit: 'seconds' }),
    'Returns true if the left duration is less than or equal to the right duration.',
  ),
  [EXPRESSION_TYPES.zonedDateTime.name]: functionFrom(
    'lte',
    [
      { name: 'left', type: EXPRESSION_TYPES.zonedDateTime },
      { name: 'right', type: EXPRESSION_TYPES.zonedDateTime },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) <= 0,
    'Returns true if the left ZonedDateTime is before or equal to the right ZonedDateTime.',
  ),
  [EXPRESSION_TYPES.plainDate.name]: functionFrom(
    'lte',
    [
      { name: 'left', type: EXPRESSION_TYPES.plainDate },
      { name: 'right', type: EXPRESSION_TYPES.plainDate },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'days' }) <= 0,
    'Returns true if the left date is before or equal to the right date.',
  ),
  [EXPRESSION_TYPES.string.name]: functionFrom(
    'lte',
    [
      { name: 'left', type: EXPRESSION_TYPES.string },
      { name: 'right', type: EXPRESSION_TYPES.string },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.localeCompare(b) <= 0,
    'Returns true if the left string is less than or equal to the right string.',
  ),
  [EXPRESSION_TYPES.number.name]: functionFrom(
    'lte',
    [
      { name: 'left', type: EXPRESSION_TYPES.number },
      { name: 'right', type: EXPRESSION_TYPES.number },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a <= b,
    'Returns true if the left number is less than or equal to the right number.',
  ),
  [EXPRESSION_TYPES.plainTime.name]: functionFrom(
    'lte',
    [
      { name: 'left', type: EXPRESSION_TYPES.plainTime },
      { name: 'right', type: EXPRESSION_TYPES.plainTime },
    ] as const,
    EXPRESSION_TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) <= 0,
    'Returns true if the left time is before or equal to the right time.',
  ),
};

export const COMPARISON_FUNCTIONS = {
  [getFunctionSignature(equals.duration)]: equals.duration,
  [getFunctionSignature(equals.zonedDateTime)]: equals.zonedDateTime,
  [getFunctionSignature(equals.plainDate)]: equals.plainDate,
  [getFunctionSignature(equals.string)]: equals.string,
  [getFunctionSignature(equals.boolean)]: equals.boolean,
  [getFunctionSignature(equals.number)]: equals.number,
  [getFunctionSignature(equals.plainTime)]: equals.plainTime,
  [getFunctionSignature(gt.duration)]: gt.duration,
  [getFunctionSignature(gt.zonedDateTime)]: gt.zonedDateTime,
  [getFunctionSignature(gt.plainDate)]: gt.plainDate,
  [getFunctionSignature(gt.string)]: gt.string,
  [getFunctionSignature(gt.number)]: gt.number,
  [getFunctionSignature(gt.plainTime)]: gt.plainTime,
  [getFunctionSignature(gte.duration)]: gte.duration,
  [getFunctionSignature(gte.zonedDateTime)]: gte.zonedDateTime,
  [getFunctionSignature(gte.plainDate)]: gte.plainDate,
  [getFunctionSignature(gte.string)]: gte.string,
  [getFunctionSignature(gte.number)]: gte.number,
  [getFunctionSignature(gte.plainTime)]: gte.plainTime,
  [getFunctionSignature(lt.duration)]: lt.duration,
  [getFunctionSignature(lt.zonedDateTime)]: lt.zonedDateTime,
  [getFunctionSignature(lt.plainDate)]: lt.plainDate,
  [getFunctionSignature(lt.string)]: lt.string,
  [getFunctionSignature(lt.number)]: lt.number,
  [getFunctionSignature(lt.plainTime)]: lt.plainTime,
  [getFunctionSignature(lte.duration)]: lte.duration,
  [getFunctionSignature(lte.zonedDateTime)]: lte.zonedDateTime,
  [getFunctionSignature(lte.plainDate)]: lte.plainDate,
  [getFunctionSignature(lte.string)]: lte.string,
  [getFunctionSignature(lte.number)]: lte.number,
  [getFunctionSignature(lte.plainTime)]: lte.plainTime,
} satisfies FunctionLibrary;
